/**
 * API Client — scaffold for the future Node/Express backend.
 *
 * Today the frontend reads/writes localStorage for every entity. When the
 * backend on the VPS goes live at `/*` (Apache reverse-proxies to
 * 127.0.0.1:3001), the per-entity helpers below become the single point
 * of migration: swap the body of each function from localStorage to
 * `request(...)` and the rest of the app keeps working.
 *
 * See: docs/api-contracts.md for the REST contract the backend must implement.
 */

import type { CatalogPlace } from '../data/places-catalog';
import type {
  CategoryDto,
  EditorialListRowDto,
  EventsListParams,
  HeroImageDto,
  MenuConfigDto,
  NeighborhoodDto,
  StaticPageDto,
} from './api-types';

// ─────────────────────────────────────────────────────────────
// Core fetch wrapper
// ─────────────────────────────────────────────────────────────

/** Logical API prefix: default `/api` (Apache → Express). Override with `VITE_API_BASE_URL` or `window.__AHG_API_BASE__`. */
function apiPrefix(): string {
  const env = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (env !== undefined && env !== '') return env.replace(/\/$/, '');
  if (typeof window !== 'undefined' && (window as any).__AHG_API_BASE__) {
    return String((window as any).__AHG_API_BASE__).replace(/\/$/, '');
  }
  return '/api';
}

/** Absolute path on current origin (or full URL if prefix is absolute). */
export function apiUrl(resourcePath: string): string {
  const p = resourcePath.startsWith('/') ? resourcePath : `/${resourcePath}`;
  const base = apiPrefix();
  if (/^https?:\/\//i.test(base)) return `${base}${p}`;
  return `${base}${p}`;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** Backend `error.code` when present (e.g. `INVALID_CREDENTIALS`, `RATE_LIMITED`). */
  code?: string;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    const envelope =
      body &&
      typeof body === 'object' &&
      'error' in body &&
      typeof (body as { error?: unknown }).error === 'object' &&
      (body as { error?: { code?: string } }).error;
    const c = envelope?.code;
    this.code = typeof c === 'string' ? c : undefined;
  }
}

/** Thrown when `fetch` fails (offline, DNS, CORS, aborted, etc.) — not an HTTP error body. */
export class NetworkError extends Error {
  constructor(message = 'Network request failed', cause?: unknown) {
    super(message);
    this.name = 'NetworkError';
    if (cause !== undefined)
      (this as Error & { cause?: unknown }).cause = cause;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: any;
  /** Auth required — when true, the call fails fast if no token is present. */
  auth?: boolean;
  /** Query params merged into the URL. */
  params?: Record<string, string | number | boolean | undefined | null>;
}

/** Reads the JWT from localStorage (`ahg_auth_token`). */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('ahg_auth_token');
}

export function setAuthToken(token: string | null) {
  if (typeof window === 'undefined') return;
  if (token) localStorage.setItem('ahg_auth_token', token);else
  localStorage.removeItem('ahg_auth_token');
}

function buildUrl(path: string, params?: RequestOptions['params']) {
  const pathname = apiUrl(path);
  const url = new URL(
    pathname,
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  );
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }
  return url.pathname + url.search;
}

export async function request<T = any>(
path: string,
options: RequestOptions = {})
: Promise<T> {
  const { body, auth, params, headers, ...rest } = options;
  const token = getAuthToken();
  if (auth && !token) {
    throw new ApiError(401, 'Authentication required — no token present.');
  }

  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body && !(body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers as Record<string, string>),
  };

  const finalBody =
  body instanceof FormData ?
  body :
  body !== undefined ?
  JSON.stringify(body) :
  undefined;

  const res = await fetch(buildUrl(path, params), {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include'
  });

  const contentType = res.headers.get('content-type') || '';
  const isJson = contentType.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const msg =
    (isJson && (payload?.error?.message || payload?.message)) ||
    (typeof payload === 'string' && payload) ||
    res.statusText ||
    'Request failed';
    throw new ApiError(res.status, msg, payload);
  }
  return payload as T;
}

export const AHG_ADMIN_PROFILE_SESSION = 'ahg_admin_profile';

/** Clears JWT + legacy admin keys + sidebar profile (sessionStorage). */
export function clearAdminSession() {
  setAuthToken(null);
  try {
    sessionStorage.removeItem(AHG_ADMIN_PROFILE_SESSION);
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem('ahg_admin_auth');
  } catch {
    /* ignore */
  }
}

interface AdminFetchInit extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: RequestOptions['params'];
}

/**
 * Admin API fetch — sends Bearer `ahg_auth_token`.
 * On 401/403 clears admin session and redirects to `/admin/login?next=<current>`.
 */
export async function adminApiFetch(
  path: string,
  options: AdminFetchInit = {},
): Promise<Response> {
  const { body, params, headers, ...rest } = options;
  const token = getAuthToken();
  const finalHeaders: Record<string, string> = {
    Accept: 'application/json',
    ...(body && !(body instanceof FormData)
      ? { 'Content-Type': 'application/json' }
      : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(headers as Record<string, string>),
  };

  const finalBody =
    body instanceof FormData ?
      body
    : body !== undefined ?
      JSON.stringify(body)
    : undefined;

  const res = await fetch(buildUrl(path, params), {
    ...rest,
    headers: finalHeaders,
    body: finalBody,
    credentials: 'include',
  });

  if (res.status === 401 || res.status === 403) {
    clearAdminSession();
    if (typeof window !== 'undefined') {
      const next = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      window.location.assign(`/admin/login?next=${next}`);
    }
  }
  return res;
}

export async function adminRequest<T = unknown>(
  path: string,
  options: AdminFetchInit & { method?: string } = {},
): Promise<T> {
  try {
    const res = await adminApiFetch(path, options);
    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');
    const payload = isJson ? await res.json().catch(() => null) : await res.text();

    if (!res.ok) {
      const msg =
        (isJson && (payload?.error?.message || payload?.message)) ||
        (typeof payload === 'string' && payload) ||
        res.statusText ||
        'Request failed';
      throw new ApiError(res.status, msg, payload);
    }

    if (res.status === 204 || payload === '' || payload === null) {
      return undefined as T;
    }
    return payload as T;
  } catch (err) {
    if (err instanceof ApiError) throw err;
    if (err instanceof NetworkError) throw err;
    const message =
      err instanceof Error ? err.message : 'Network request failed';
    throw new NetworkError(message, err);
  }
}

// ─────────────────────────────────────────────────────────────
// Shared response shapes
// ─────────────────────────────────────────────────────────────

/** Matches shipped Express list envelope (`meta.limit`, not `pageSize`). */
export interface Paginated<T> {
  data: T[];
  meta: {
    page: number;
    limit: number;
    total: number;
  };
}

export interface ListQuery {
  page?: number;
  /** Alias sent to API as `limit`. */
  pageSize?: number;
  limit?: number;
  q?: string;
  sort?: string;
  /** Free-form filters — backend interprets per resource. */
  filters?: Record<string, string | number | boolean | undefined>;
}

function spreadList(q?: ListQuery): RequestOptions['params'] {
  if (!q) return undefined;
  const { filters, pageSize, limit, ...rest } = q;
  const lim = limit ?? pageSize;
  const mapped: Record<string, string | number | boolean> = {
    ...rest,
    ...(lim != null ? { limit: lim } : {}),
  };
  return { ...mapped, ...(filters || {}) };
}

export interface PlacesListParams extends ListQuery {
  category?: string;
  neighborhood?: string;
  priceBand?: string;
  openNow?: boolean;
  seaView?: boolean;
  editorsPicks?: boolean;
  hiddenGems?: boolean;
  near?: string;
  /** Admin catalog list (`GET /api/admin/places`) — soft-deleted rows. */
  includeDeleted?: boolean;
}

/** Max `limit` allowed on public `GET /api/places` (see `PaginationQuery` on API). */
const PUBLIC_PLACES_PAGE_SIZE = 100;

export async function getPlaces(
  params?: PlacesListParams
): Promise<Paginated<CatalogPlace>> {
  return request<Paginated<CatalogPlace>>('/places', {
    params: spreadList(params),
  });
}

/** Fetches every published place by paging (100 rows per request). */
export async function getPlacesAllPages(
  params?: Omit<PlacesListParams, 'page' | 'limit' | 'pageSize'>,
): Promise<CatalogPlace[]> {
  const limit = PUBLIC_PLACES_PAGE_SIZE;
  const out: CatalogPlace[] = [];
  let page = 1;
  for (;;) {
    const res = await getPlaces({ ...params, limit, page });
    const chunk = res.data as CatalogPlace[];
    out.push(...chunk);
    const total = res.meta.total;
    if (out.length >= total || chunk.length < limit) break;
    page += 1;
    if (page > 200) break;
  }
  return out;
}

export async function adminListPlaces(
  params?: PlacesListParams,
): Promise<Paginated<CatalogPlace>> {
  return adminRequest<Paginated<CatalogPlace>>('/admin/places', {
    params: spreadList(params),
  });
}

export async function adminGetPlace(id: string): Promise<Record<string, unknown>> {
  return adminRequest<Record<string, unknown>>(
    `/admin/places/${encodeURIComponent(id)}`,
  );
}

export async function adminCreatePlace(input: Record<string, unknown>) {
  return adminRequest<Record<string, unknown>>('/places', {
    method: 'POST',
    body: input,
  });
}

export async function adminUpdatePlace(id: string, input: Record<string, unknown>) {
  return adminRequest<Record<string, unknown>>(
    `/places/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: input,
    },
  );
}

export async function adminDeletePlace(id: string): Promise<void> {
  await adminRequest(`/places/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function adminUploadPlaceImage(
  placeId: string,
  file: File,
  purpose?: 'gallery' | 'brandLogo',
): Promise<{ url: string; place: Record<string, unknown> }> {
  const fd = new FormData();
  fd.append('file', file);
  const params =
    purpose === 'brandLogo' ? { purpose: 'brandLogo' } : undefined;
  return adminRequest(`/places/${encodeURIComponent(placeId)}/images`, {
    method: 'POST',
    body: fd,
    params,
  });
}

// ─────────────────────────────────────────────────────────────
// Admin — neighborhoods / itineraries / editorial / events / street-food / CMS / moderation / categories
// ─────────────────────────────────────────────────────────────

export async function adminListNeighborhoods(
  params?: ListQuery,
): Promise<Paginated<NeighborhoodDto>> {
  return adminRequest<Paginated<NeighborhoodDto>>('/neighborhoods', {
    params: spreadList(params),
  });
}

export async function adminCreateNeighborhood(
  input: Record<string, unknown>,
): Promise<NeighborhoodDto> {
  return adminRequest(`/neighborhoods`, { method: 'POST', body: input });
}

export async function adminUpdateNeighborhood(
  slug: string,
  input: Record<string, unknown>,
): Promise<NeighborhoodDto> {
  return adminRequest(`/neighborhoods/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteNeighborhood(slug: string): Promise<void> {
  await adminRequest(`/neighborhoods/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

export async function adminListItineraries(
  params?: ListQuery,
): Promise<Paginated<Record<string, unknown>>> {
  return adminRequest(`/itineraries`, { params: spreadList(params) });
}

export async function adminCreateItinerary(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/itineraries`, { method: 'POST', body: input });
}

export async function adminUpdateItinerary(
  id: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/itineraries/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteItinerary(id: string): Promise<void> {
  await adminRequest(`/itineraries/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function adminListEditorial(
  params?: ListQuery,
): Promise<Paginated<EditorialListRowDto>> {
  return adminRequest(`/editorial`, { params: spreadList(params) });
}

export async function adminGetEditorialPost(
  slug: string,
): Promise<Record<string, unknown>> {
  return adminRequest<Record<string, unknown>>(
    `/editorial/${encodeURIComponent(slug)}`,
  );
}

export async function adminCreateEditorialPost(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/editorial`, { method: 'POST', body: input });
}

export async function adminUpdateEditorialPost(
  slug: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/editorial/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteEditorialPost(slug: string): Promise<void> {
  await adminRequest(`/editorial/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

/** Sets `publishedAt` — ISO-8601 with offset (e.g. `new Date().toISOString()` to publish). */
export async function adminPublishEditorialPost(
  slug: string,
  publishedAt: string,
): Promise<Record<string, unknown>> {
  return adminUpdateEditorialPost(slug, { publishedAt });
}

export async function adminCreateEditorial(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminCreateEditorialPost(input);
}

export async function adminUpdateEditorial(
  slug: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminUpdateEditorialPost(slug, input);
}

export async function adminDeleteEditorial(slug: string): Promise<void> {
  return adminDeleteEditorialPost(slug);
}

export async function adminListEvents(
  params?: ListQuery & EventsListParams,
): Promise<Paginated<Record<string, unknown>>> {
  return adminRequest(`/events`, { params: spreadList(params) });
}

export async function adminGetEvent(
  idOrSlug: string,
): Promise<Record<string, unknown>> {
  return adminRequest<Record<string, unknown>>(
    `/events/${encodeURIComponent(idOrSlug)}`,
  );
}

export async function adminCreateEvent(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/events`, { method: 'POST', body: input });
}

export async function adminUpdateEvent(
  idOrSlug: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/events/${encodeURIComponent(idOrSlug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteEvent(id: string): Promise<void> {
  await adminRequest(`/events/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function adminListStreetFood(
  params?: ListQuery,
): Promise<Paginated<Record<string, unknown>>> {
  return adminRequest(`/street-food`, { params: spreadList(params) });
}

export async function adminGetStreetFoodVendor(
  idOrSlug: string,
): Promise<Record<string, unknown>> {
  return adminRequest<Record<string, unknown>>(
    `/street-food/${encodeURIComponent(idOrSlug)}`,
  );
}

export async function adminCreateStreetFoodVendor(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/street-food`, { method: 'POST', body: input });
}

export async function adminUpdateStreetFoodVendor(
  idOrSlug: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/street-food/${encodeURIComponent(idOrSlug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminCreateStreetFood(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminCreateStreetFoodVendor(input);
}

export async function adminUpdateStreetFood(
  id: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminUpdateStreetFoodVendor(id, input);
}

export async function adminDeleteStreetFood(id: string): Promise<void> {
  await adminRequest(`/street-food/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function adminListStaticPages(): Promise<
  Paginated<StaticPageDto>
> {
  return adminRequest(`/admin/static-pages`);
}

export async function adminCreateStaticPage(
  input: Record<string, unknown>,
): Promise<StaticPageDto> {
  return adminRequest(`/admin/static-pages`, {
    method: 'POST',
    body: input,
  });
}

export async function adminUpdateStaticPage(
  slug: string,
  input: Record<string, unknown>,
): Promise<StaticPageDto> {
  return adminRequest(`/admin/static-pages/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteStaticPage(slug: string): Promise<void> {
  await adminRequest(`/admin/static-pages/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

export async function adminPutHeroImages(body: {
  images: unknown[];
}): Promise<unknown> {
  return adminRequest(`/hero-images`, { method: 'PUT', body });
}

export async function adminPutMenuConfig(
  input: Record<string, unknown>,
): Promise<MenuConfigDto> {
  return adminRequest(`/menu-config`, { method: 'PUT', body: input });
}

export async function adminCreateCategoryMeta(
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/admin/categories`, { method: 'POST', body: input });
}

export async function adminPatchCategoryMeta(
  slug: string,
  input: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminRequest(`/admin/categories/${encodeURIComponent(slug)}`, {
    method: 'PATCH',
    body: input,
  });
}

export async function adminDeleteCategoryMeta(slug: string): Promise<void> {
  await adminRequest(`/admin/categories/${encodeURIComponent(slug)}`, {
    method: 'DELETE',
  });
}

export interface AdminReviewRow {
  id: string;
  placeId: string;
  placeName: unknown;
  userId: string;
  userEmail: string;
  userDisplayName: string;
  rating: number;
  text: unknown;
  status: string;
  createdAt: string;
}

export async function adminListReviews(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<AdminReviewRow>> {
  return adminRequest(`/admin/reviews`, {
    params: spreadList(params),
  });
}

export async function adminPatchReview(
  id: string,
  body: { status: 'APPROVED' | 'REJECTED' },
): Promise<{ ok: boolean; id: string; status: string }> {
  return adminRequest(`/admin/reviews/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export async function adminListCorrections(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<Record<string, unknown>>> {
  return adminRequest(`/admin/corrections`, { params: spreadList(params) });
}

export async function adminPatchCorrection(
  id: string,
  body: { status: 'APPLIED' | 'DISMISSED' },
): Promise<{ ok: boolean; id: string; status: string }> {
  return adminRequest(`/admin/corrections/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export async function adminListGemSubmissions(params?: {
  status?: string;
  page?: number;
  limit?: number;
}): Promise<Paginated<Record<string, unknown>>> {
  return adminRequest(`/admin/submissions`, { params: spreadList(params) });
}

export async function adminPatchGemSubmission(
  id: string,
  body: { status: 'APPROVED' | 'REJECTED' },
): Promise<{ ok: boolean; id: string; status: string; approvedPlaceId?: string | null }> {
  return adminRequest(`/admin/submissions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body,
  });
}

export async function getPlace(id: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(`/places/${encodeURIComponent(id)}`);
}

export async function searchPlaces(q: string): Promise<
  Array<{
    id: string;
    name: { en: string; ar: string };
    coverImage: string;
    categoryLabel: { en: string; ar: string };
  }>
> {
  return request(`/places/search`, { params: { q } });
}

/** Paginated neighborhood catalog */
export async function getNeighborhoodsList(
  params?: ListQuery,
): Promise<Paginated<NeighborhoodDto>> {
  return request<Paginated<NeighborhoodDto>>('/neighborhoods', {
    params: spreadList(params),
  });
}

export async function getNeighborhood(slug: string): Promise<NeighborhoodDto> {
  return request<NeighborhoodDto>(
    `/neighborhoods/${encodeURIComponent(slug)}`,
  );
}

export async function getNeighborhoodPlaces(
  slug: string,
  params?: PlacesListParams,
): Promise<Paginated<CatalogPlace>> {
  return request<Paginated<CatalogPlace>>(
    `/neighborhoods/${encodeURIComponent(slug)}/places`,
    { params: spreadList(params) },
  );
}

export async function getItineraries(
  params?: ListQuery,
): Promise<Paginated<Record<string, unknown>>> {
  return request<Paginated<Record<string, unknown>>>('/itineraries', {
    params: spreadList(params),
  });
}

export async function getItinerary(id: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `/itineraries/${encodeURIComponent(id)}`,
  );
}

export async function getEditorialList(
  params?: ListQuery,
): Promise<Paginated<EditorialListRowDto>> {
  return request<Paginated<EditorialListRowDto>>('/editorial', {
    params: spreadList(params),
  });
}

export async function getEditorialArticle(
  slug: string,
): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `/editorial/${encodeURIComponent(slug)}`,
  );
}

export async function getEventsList(
  params?: ListQuery & EventsListParams,
): Promise<Paginated<Record<string, unknown>>> {
  return request<Paginated<Record<string, unknown>>>('/events', {
    params: spreadList(params),
  });
}

export async function getEvent(id: string): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(`/events/${encodeURIComponent(id)}`);
}

export async function getStreetFoodList(
  params?: ListQuery,
): Promise<Paginated<Record<string, unknown>>> {
  return request<Paginated<Record<string, unknown>>>('/street-food', {
    params: spreadList(params),
  });
}

export async function getStreetFoodVendor(
  id: string,
): Promise<Record<string, unknown>> {
  return request<Record<string, unknown>>(
    `/street-food/${encodeURIComponent(id)}`,
  );
}

export async function getHeroImages(): Promise<Paginated<HeroImageDto>> {
  return request<Paginated<HeroImageDto>>('/hero-images');
}

export async function getMenuConfig(): Promise<MenuConfigDto> {
  return request<MenuConfigDto>('/menu-config');
}

export async function getPublicStaticPage(
  slug: string,
): Promise<StaticPageDto> {
  return request<StaticPageDto>(
    `/static-pages/${encodeURIComponent(slug)}`,
  );
}

export async function getCategories(): Promise<Paginated<CategoryDto>> {
  return request<Paginated<CategoryDto>>('/categories');
}

// ─────────────────────────────────────────────────────────────
// Auth
// ─────────────────────────────────────────────────────────────

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  avatar?: string;
  role: 'user' | 'editor' | 'admin';
  emailVerified: boolean;
  createdAt: string;
}

export interface AuthResponse {
  user: AuthUser;
  token: string;
  expiresAt: string;
}

export interface VerifyEmailResponse {
  ok: true;
  user: AuthUser;
}

export async function authSignup(input: {
  email: string;
  password: string;
  displayName: string;
}) {
  return request<AuthResponse>('/auth/signup', { method: 'POST', body: input });
}

export async function authSignin(email: string, password: string) {
  return request<AuthResponse>('/auth/signin', {
    method: 'POST',
    body: { email, password },
  });
}

export async function authSignout() {
  return request<{ ok: boolean }>('/auth/signout', { method: 'POST', auth: true });
}

export async function authMe() {
  return request<AuthUser>('/auth/me', { auth: true });
}

export async function authForgotPassword(email: string) {
  return request<{ ok: boolean }>('/auth/forgot-password', {
    method: 'POST',
    body: { email },
  });
}

export async function authResetPassword(token: string, newPassword: string) {
  return request<{ ok: boolean }>('/auth/reset-password', {
    method: 'POST',
    body: { token, newPassword },
  });
}

export async function authVerifyEmail(token: string) {
  return request<VerifyEmailResponse>('/auth/verify-email', {
    method: 'POST',
    body: { token },
  });
}

export const auth = {
  signIn: authSignin,
  signUp: authSignup,
  signOut: authSignout,
  me: authMe,
  forgotPassword: authForgotPassword,
  resetPassword: authResetPassword,
  verifyEmail: authVerifyEmail,
  // Admin
  adminLogin: (email: string, password: string) =>
    request<AuthResponse>('/admin/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
};

// ─────────────────────────────────────────────────────────────
// Places
// ─────────────────────────────────────────────────────────────

export const places = {
  list: (q?: PlacesListParams) =>
  request<Paginated<CatalogPlace>>('/places', { params: spreadList(q) }),
  get: (id: string) => getPlace(id),
  search: (q: string) => searchPlaces(q),
  // Admin
  create: (input: any) =>
  request<any>('/places', { method: 'POST', body: input, auth: true }),
  update: (id: string, input: any) =>
  request<any>(`/places/${id}`, {
    method: 'PATCH',
    body: input,
    auth: true
  }),
  remove: (id: string) =>
  request<void>(`/places/${id}`, { method: 'DELETE', auth: true }),
  uploadImage: (id: string, file: File, purpose?: 'gallery' | 'brandLogo') => {
    const fd = new FormData();
    fd.append('file', file);
    const params =
      purpose === 'brandLogo' ? { purpose: 'brandLogo' } : undefined;
    return request<{ url: string; place: Record<string, unknown> }>(
      `/places/${id}/images`,
      {
        method: 'POST',
        body: fd,
        auth: true,
        params,
      },
    );
  },
};

// ─────────────────────────────────────────────────────────────
// Street food vendors
// ─────────────────────────────────────────────────────────────

export const streetFood = {
  list: (q?: ListQuery) => getStreetFoodList(q),
  get: (id: string) => getStreetFoodVendor(id),
  create: (input: any) =>
  request<any>('/street-food', {
    method: 'POST',
    body: input,
    auth: true
  }),
  update: (id: string, input: any) =>
  request<any>(`/street-food/${id}`, {
    method: 'PATCH',
    body: input,
    auth: true
  }),
  remove: (id: string) =>
  request<void>(`/street-food/${id}`, { method: 'DELETE', auth: true })
};

// ─────────────────────────────────────────────────────────────
// Neighborhoods
// ─────────────────────────────────────────────────────────────

export const neighborhoods = {
  list: (q?: ListQuery) => getNeighborhoodsList(q),
  get: (slug: string) => getNeighborhood(slug),
  places: (slug: string, q?: PlacesListParams) =>
    getNeighborhoodPlaces(slug, q),
  create: (input: any) =>
  request<any>('/neighborhoods', {
    method: 'POST',
    body: input,
    auth: true
  }),
  update: (slug: string, input: any) =>
  request<any>(`/neighborhoods/${slug}`, {
    method: 'PATCH',
    body: input,
    auth: true
  }),
  remove: (slug: string) =>
  request<void>(`/neighborhoods/${slug}`, {
    method: 'DELETE',
    auth: true
  })
};

// ─────────────────────────────────────────────────────────────
// Itineraries
// ─────────────────────────────────────────────────────────────

export const itineraries = {
  list: (q?: ListQuery) => getItineraries(q),
  get: (id: string) => getItinerary(id),
  create: (input: any) =>
  request<any>('/itineraries', {
    method: 'POST',
    body: input,
    auth: true
  }),
  update: (id: string, input: any) =>
  request<any>(`/itineraries/${id}`, {
    method: 'PATCH',
    body: input,
    auth: true
  }),
  remove: (id: string) =>
  request<void>(`/itineraries/${id}`, { method: 'DELETE', auth: true })
};

// ─────────────────────────────────────────────────────────────
// Editorial articles
// ─────────────────────────────────────────────────────────────

export const editorial = {
  list: (q?: ListQuery) => getEditorialList(q),
  get: (slug: string) => getEditorialArticle(slug),
  create: (input: Record<string, unknown>) => adminCreateEditorialPost(input),
  update: (slug: string, input: Record<string, unknown>) =>
    adminUpdateEditorialPost(slug, input),
  remove: (slug: string) => adminDeleteEditorialPost(slug),
};

// ─────────────────────────────────────────────────────────────
// Events
// ─────────────────────────────────────────────────────────────

export const events = {
  list: (q?: ListQuery & EventsListParams) => getEventsList(q),
  get: (id: string) => getEvent(id),
  create: (input: any) =>
  request<any>('/events', { method: 'POST', body: input, auth: true }),
  update: (id: string, input: any) =>
  request<any>(`/events/${id}`, {
    method: 'PATCH',
    body: input,
    auth: true
  }),
  remove: (id: string) =>
  request<void>(`/events/${id}`, { method: 'DELETE', auth: true })
};

// ─────────────────────────────────────────────────────────────
// Static pages (CMS — about/contact/privacy/etc)
// ─────────────────────────────────────────────────────────────

export const staticPages = {
  /** Prefer `adminListStaticPages` / `adminCreateStaticPage` for CMS admin. */
  listAdmin: () => adminListStaticPages(),
  get: (slug: string) => getPublicStaticPage(slug),
  create: (input: Record<string, unknown>) => adminCreateStaticPage(input),
  update: (slug: string, input: Record<string, unknown>) =>
    adminUpdateStaticPage(slug, input),
  remove: (slug: string) => adminDeleteStaticPage(slug),
};

// ─────────────────────────────────────────────────────────────
// Menus (header nav + footer columns)
// ─────────────────────────────────────────────────────────────

export const menus = {
  get: () => getMenuConfig(),
  update: (input: Record<string, unknown>) => adminPutMenuConfig(input),
};

// ─────────────────────────────────────────────────────────────
// Hero images
// ─────────────────────────────────────────────────────────────

export const heroImages = {
  list: () => getHeroImages(),
  set: (images: unknown[]) =>
    adminPutHeroImages({ images }),
};

// ─────────────────────────────────────────────────────────────
// UGC — favorites (authenticated)
// ─────────────────────────────────────────────────────────────

export async function fetchMyFavoritePlaceIds(): Promise<string[]> {
  const res = await request<{ data: string[] }>('/me/favorites', {
    auth: true,
  });
  return res.data;
}

export async function addMyFavoritePlace(placeId: string): Promise<void> {
  await request<{ ok: boolean }>(
    `/me/favorites/${encodeURIComponent(placeId)}`,
    { method: 'POST', auth: true },
  );
}

export async function removeMyFavoritePlace(placeId: string): Promise<void> {
  await request<{ ok: boolean }>(
    `/me/favorites/${encodeURIComponent(placeId)}`,
    { method: 'DELETE', auth: true },
  );
}

// ─────────────────────────────────────────────────────────────
// UGC — trip plans (authenticated)
// ─────────────────────────────────────────────────────────────

export interface TripPlanApiDto {
  id: string;
  name: string;
  startDate: string | null;
  days: unknown[];
  sortByLocation: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function fetchMyTripPlans(): Promise<TripPlanApiDto[]> {
  const res = await request<{ data: TripPlanApiDto[] }>('/me/trip-plans', {
    auth: true,
  });
  return res.data;
}

export async function fetchMyTripPlan(id: string): Promise<TripPlanApiDto> {
  return request<TripPlanApiDto>(
    `/me/trip-plans/${encodeURIComponent(id)}`,
    { auth: true },
  );
}

export async function createMyTripPlan(body: {
  name: string;
  startDate?: string;
  days: unknown[];
  sortByLocation?: boolean;
}): Promise<TripPlanApiDto> {
  return request<TripPlanApiDto>('/me/trip-plans', {
    method: 'POST',
    body,
    auth: true,
  });
}

export async function patchMyTripPlan(
  id: string,
  body: Partial<{
    name: string;
    startDate?: string | null;
    days: unknown[];
    sortByLocation: boolean;
  }>,
): Promise<TripPlanApiDto> {
  return request<TripPlanApiDto>(
    `/me/trip-plans/${encodeURIComponent(id)}`,
    { method: 'PATCH', body, auth: true },
  );
}

export async function deleteMyTripPlan(id: string): Promise<void> {
  await request<{ ok: boolean }>(
    `/me/trip-plans/${encodeURIComponent(id)}`,
    { method: 'DELETE', auth: true },
  );
}

// ─────────────────────────────────────────────────────────────
// UGC — reviews
// ─────────────────────────────────────────────────────────────

export interface PlaceReviewPublicDto {
  id: string;
  rating: number;
  text: { en: string; ar: string };
  createdAt: string;
  author: string;
  avatar: string;
}

export async function fetchApprovedReviewsForPlace(
  placeId: string,
  params?: ListQuery,
): Promise<Paginated<PlaceReviewPublicDto>> {
  return request<Paginated<PlaceReviewPublicDto>>(
    `/places/${encodeURIComponent(placeId)}/reviews`,
    {
      params: {
        ...spreadList(params),
        status: 'APPROVED',
      },
    },
  );
}

export async function submitPlaceReview(
  placeId: string,
  input: { rating: number; text: { en: string; ar: string } },
): Promise<{ id: string; status: string; createdAt: string }> {
  return request(`/places/${encodeURIComponent(placeId)}/reviews`, {
    method: 'POST',
    body: input,
    auth: true,
  });
}

export interface MyReviewDto {
  id: string;
  placeId: string;
  placeName: { en: string; ar: string };
  rating: number;
  text: { en: string; ar: string };
  status: string;
  createdAt: string;
  updatedAt: string;
}

export async function fetchMyReviews(): Promise<MyReviewDto[]> {
  const res = await request<{ data: MyReviewDto[] }>('/me/reviews', {
    auth: true,
  });
  return res.data;
}

// ─────────────────────────────────────────────────────────────
// UGC — corrections / submissions / contact
// ─────────────────────────────────────────────────────────────

export async function submitPlaceCorrection(
  placeId: string,
  input: { field: string; suggestion: string; note?: string },
): Promise<void> {
  await request(`/places/${encodeURIComponent(placeId)}/corrections`, {
    method: 'POST',
    body: input,
    auth: true,
  });
}

export async function submitGemPayload(input: {
  name: { en: string; ar: string };
  category: string;
  neighborhood: { en: string; ar: string };
  whyHidden: { en: string; ar: string };
  contact?: { name?: string; email?: string; phone?: string };
  images?: string[];
}): Promise<{ id: string; createdAt: string }> {
  return request('/submissions', { method: 'POST', body: input, auth: true });
}

export interface MyGemSubmissionDto {
  id: string;
  name: { en: string; ar: string };
  category: string;
  neighborhood: { en: string; ar: string };
  whyHidden: { en: string; ar: string };
  status: string;
  createdAt: string;
}

export async function fetchMySubmissions(): Promise<MyGemSubmissionDto[]> {
  const res = await request<{ data: MyGemSubmissionDto[] }>(
    '/me/submissions',
    { auth: true },
  );
  return res.data;
}

export async function postContactMessage(input: {
  name: string;
  email: string;
  subject: string;
  message: string;
  /** Honeypot — must stay empty for humans */
  website?: string;
}): Promise<{ ok: true }> {
  return request<{ ok: true }>('/contact', { method: 'POST', body: input });
}

/** @deprecated Prefer named helpers above */
export const reviews = {
  listForPlace: (placeId: string) =>
    fetchApprovedReviewsForPlace(placeId).then((r) => r.data),
  submit: submitPlaceReview,
};

/** @deprecated Prefer submitPlaceCorrection */
export const corrections = {
  submit: submitPlaceCorrection,
};

/** @deprecated Prefer submitGemPayload / fetchMySubmissions */
export const submissions = {
  submitGem: submitGemPayload,
};

/** @deprecated Prefer trip plan helpers above */
export const tripPlans = {
  list: fetchMyTripPlans,
  get: fetchMyTripPlan,
  create: createMyTripPlan,
  update: patchMyTripPlan,
  remove: deleteMyTripPlan,
};

/** @deprecated Prefer favorite helpers above */
export const favorites = {
  list: fetchMyFavoritePlaceIds,
  add: addMyFavoritePlace,
  remove: removeMyFavoritePlace,
};

/** @deprecated Prefer postContactMessage */
export const contact = {
  send: postContactMessage,
};

// ─────────────────────────────────────────────────────────────
// Admin — bulk place import (Google + OpenAI)
// ─────────────────────────────────────────────────────────────

export interface ImportJobSummaryDto {
  id: string;
  query: string;
  status: string;
  totalCandidates: number;
  selectedCount: number;
  enrichedCount: number;
  importedCount: number;
  createdAt: string;
}

export interface ImportCandidateListItemDto {
  id: string;
  name: string;
  address: string;
  /** From Google search / DB; may be null if invalid. */
  lat: number | null;
  lng: number | null;
  rating: number | null;
  userRatingsTotal: number | null;
  types: string[];
  status: string;
}

export interface ImportSearchResponse {
  job: ImportJobSummaryDto;
  candidates: ImportCandidateListItemDto[];
}

export interface ImportJobsListResponse {
  jobs: ImportJobSummaryDto[];
  pagination: { total: number; page: number; pages: number };
}

export interface ImportJobDetailCandidate extends ImportCandidateListItemDto {
  createdAt: string;
  enrichedAt: string | null;
  importedAt: string | null;
  createdPlaceId: string | null;
  /**
   * Public catalog slug for `/places/:id` when the linked place exists and is not soft-deleted.
   * When imported but null, the draft is not publicly visible (removed/hidden).
   */
  publicPlaceId?: string | null;
  /** Present on full job payload; used by review UI. */
  enrichedData?: unknown;
  rawGoogleData?: unknown;
  photoUrls?: string[];
  isDuplicate?: boolean;
}

export interface ImportJobDetailResponse {
  job: ImportJobSummaryDto & {
    neighborhoodHint: string | null;
    categoryHint: string | null;
    googleApiCalls: number;
    openaiTokensUsed: number;
    estimatedCostUsd: string;
  };
  candidates: ImportJobDetailCandidate[];
}

export interface ImportSelectResponse {
  success: boolean;
  selectedCount: number;
  estimatedCost: number;
}

export interface ImportEnrichStartResponse {
  success: boolean;
  message: string;
  jobId: string;
}

export interface ImportCandidateDetailDto {
  id: string;
  name: string;
  status: string;
  rawGoogleData: unknown;
  enrichedData: unknown;
  photoUrls: string[];
}

export interface ImportPublishResponse {
  success: boolean;
  placeId: string;
  placeSlug: string;
}

/** Aliases for admin bulk-import UI (Magic Patterns mirror). */
export type ApiImportJob = ImportJobSummaryDto;
export type ApiImportCandidate = ImportJobDetailCandidate;

async function importListJobsPaginated(
  params?: { page?: number; limit?: number },
): Promise<Paginated<ImportJobSummaryDto>> {
  const raw = await adminRequest<ImportJobsListResponse>('/admin/import/jobs', {
    params,
  });
  const page = raw.pagination.page;
  const limit = params?.limit ?? 24;
  return {
    data: raw.jobs,
    meta: {
      page,
      limit,
      total: raw.pagination.total,
    },
  };
}

/** Bulk place import — `/api/admin/import/*` (admin JWT). */
export const imports = {
  search: (body: {
    query: string;
    neighborhoodHint?: string | null;
    categoryHint?: string | null;
  }) =>
    adminRequest<ImportSearchResponse>('/admin/import/search', {
      method: 'POST',
      body,
    }),

  /** List jobs — returns `{ data, meta }` like other admin lists. */
  listJobs: importListJobsPaginated,

  getJob: (id: string) =>
    adminRequest<ImportJobDetailResponse>(
      `/admin/import/jobs/${encodeURIComponent(id)}`,
    ),

  select: (
    jobId: string,
    candidateIds: string[],
    options?: { confirmHighCost?: boolean },
  ) =>
    adminRequest<ImportSelectResponse>(
      `/admin/import/jobs/${encodeURIComponent(jobId)}/select`,
      {
        method: 'POST',
        body: { candidateIds },
        ...(options?.confirmHighCost
          ? { headers: { 'X-Import-Cost-Confirm': '1' } }
          : {}),
      },
    ),

  selectCandidates: (
    jobId: string,
    candidateIds: string[],
    options?: { confirmHighCost?: boolean },
  ) =>
    adminRequest<ImportSelectResponse>(
      `/admin/import/jobs/${encodeURIComponent(jobId)}/select`,
      {
        method: 'POST',
        body: { candidateIds },
        ...(options?.confirmHighCost
          ? { headers: { 'X-Import-Cost-Confirm': '1' } }
          : {}),
      },
    ),

  enrich: (jobId: string) =>
    adminRequest<ImportEnrichStartResponse>(
      `/admin/import/jobs/${encodeURIComponent(jobId)}/enrich`,
      { method: 'POST' },
    ),

  enrichJob: (jobId: string) =>
    adminRequest<ImportEnrichStartResponse>(
      `/admin/import/jobs/${encodeURIComponent(jobId)}/enrich`,
      { method: 'POST' },
    ),

  getCandidate: (id: string) =>
    adminRequest<ImportCandidateDetailDto>(
      `/admin/import/candidates/${encodeURIComponent(id)}`,
    ),

  patchCandidate: (id: string, enrichedData: Record<string, unknown>) =>
    adminRequest<ImportCandidateDetailDto>(
      `/admin/import/candidates/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: { enrichedData },
      },
    ),

  updateCandidate: (
    id: string,
    body: { enrichedData: Record<string, unknown> },
  ) =>
    adminRequest<ImportCandidateDetailDto>(
      `/admin/import/candidates/${encodeURIComponent(id)}`,
      {
        method: 'PATCH',
        body: { enrichedData: body.enrichedData },
      },
    ),

  publishCandidate: (
    id: string,
    body?: {
      acceptDuplicate?: boolean
    },
  ) =>
    adminRequest<ImportPublishResponse>(
      `/admin/import/candidates/${encodeURIComponent(id)}/publish`,
      {
        method: 'POST',
        body: body ?? {},
      },
    ),

  addDraftPlaceFromImport: (
    id: string,
    body?: {
      acceptDuplicate?: boolean
    },
  ) =>
    adminRequest<ImportPublishResponse>(
      `/admin/import/candidates/${encodeURIComponent(id)}/add-draft-place`,
      {
        method: 'POST',
        body: body ?? {},
      },
    ),

  rejectCandidate: (id: string) =>
    adminRequest<{ success: boolean }>(
      `/admin/import/candidates/${encodeURIComponent(id)}/reject`,
      { method: 'POST' },
    ),
};

// ─────────────────────────────────────────────────────────────
// Ollama-backed AI helpers (translation / tagline / semantic search)
// ─────────────────────────────────────────────────────────────

export const ai = {
  translate: (input: {text: string;from: 'en' | 'ar';to: 'en' | 'ar';}) =>
  request<{translation: string;}>('/ai/translate', {
    method: 'POST',
    body: input,
    auth: true
  }),
  generateTagline: (input: {
    name: string;
    category: string;
    lang: 'en' | 'ar';
  }) =>
  request<{tagline: string;}>('/ai/tagline', {
    method: 'POST',
    body: input,
    auth: true
  }),
  semanticSearch: (query: string, lang: 'en' | 'ar') =>
  request<{results: Array<{id: string;score: number;}>;}>(
    '/ai/search',
    { method: 'POST', body: { query, lang } }
  )
};

// ─────────────────────────────────────────────────────────────
// Unified default export
// ─────────────────────────────────────────────────────────────

export const api = {
  auth,
  authSignup,
  authSignin,
  authSignout,
  authMe,
  authForgotPassword,
  authResetPassword,
  authVerifyEmail,
  places,
  streetFood,
  neighborhoods,
  itineraries,
  editorial,
  events,
  staticPages,
  menus,
  heroImages,
  reviews,
  corrections,
  submissions,
  tripPlans,
  favorites,
  contact,
  imports,
  ai,
  getPlaces,
  getPlacesAllPages,
  getPlace,
  searchPlaces,
  getNeighborhoodsList,
  getNeighborhood,
  getNeighborhoodPlaces,
  getItineraries,
  getItinerary,
  getEditorialList,
  getEditorialArticle,
  getEventsList,
  getEvent,
  getStreetFoodList,
  getStreetFoodVendor,
  getHeroImages,
  getMenuConfig,
  getPublicStaticPage,
  getCategories,
};

export default api;